/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { QueryKey } from 'react-query/types/core/types';
import ILocalQueryData from './Data.types';

type TKey = keyof ILocalQueryData;
type TData<K extends TKey> = ILocalQueryData[K];

const getQueryKey = (queryKey: QueryKey): QueryKey => ['__data', queryKey];

export const useGetData = <K extends TKey>(
  key: K,
  options?: { initialData?: TData<K>; cacheTime?: number }
): TData<K> /*| undefined*/ => {
  const queryKey: QueryKey = getQueryKey(key);
  const queryClient = useQueryClient();
  const { data } = useQuery<TData<K>>(
    queryKey,
    () => queryClient.getQueryData(queryKey) as TData<K>,
    {
      initialData: options?.initialData,
      cacheTime: options?.cacheTime ?? Infinity,
      staleTime: Infinity,
      keepPreviousData: true,
      notifyOnChangeProps: ['data'],
    }
  );
  return data!;
};

export const useSetData = <K extends TKey>(
  key: K
): ((__data: Partial<TData<K>>) => void) => {
  const queryKey: QueryKey = getQueryKey(key);
  const queryClient = useQueryClient();

  return useCallback(
    (data) => {
      const isObject: boolean =
        data && typeof data === 'object' && !Array.isArray(data);
      const prevData: typeof data = queryClient.getQueryData(queryKey) || {};

      queryClient.setQueryData(
        queryKey,
        isObject ? { ...prevData, ...data } : data
      );
      queryClient.invalidateQueries(queryKey).then();
    },
    [queryKey]
  );
};

export const useRemoveData = <K extends TKey>(key: K): (() => void) => {
  const queryKey: QueryKey = getQueryKey(key);
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.removeQueries(queryKey), [queryKey]);
};

export const useResetData = <K extends TKey>(key: K): (() => void) => {
  const queryKey: QueryKey = getQueryKey(key);
  const queryClient = useQueryClient();
  return useCallback(() => queryClient.resetQueries(queryKey), [queryKey]);
};

export const useData = <K extends TKey>(
  queryKey: K,
  options?: { initialData?: TData<K>; cacheTime?: number }
) => {
  return {
    data: useGetData<K>(queryKey, {
      initialData: options?.initialData,
      cacheTime: options?.cacheTime,
    }),
    setData: useSetData<K>(queryKey),
    removeData: useRemoveData<K>(queryKey),
    resetData: useResetData<K>(queryKey),
  };
};

export default useData;
